/**
 * Owns the one signature panel a document view needs. Rows call
 * `useSignaturePanel()?.open(...)` instead of each rendering their own panel —
 * virtualised rows unmount as you scroll, which would tear an open panel down.
 *
 * Mounted by `DocumentListTable` (Folders, List, share page, container dialog)
 * and by `ServerDocumentTimeline`. A row rendered outside a provider still shows
 * its signed indicators, they just aren't clickable.
 *
 * It also owns the viewer the panel opens enclosed documents in. Those
 * documents are deliberately absent from every document list (the container is
 * the row), so no view holds a row to preview them from — the panel carries the
 * storage fields itself and opens the same `DocumentViewerDialog` the lists use.
 * A view that has rows of its own to open (the Signing page) opens them in this
 * same viewer through `useSignaturePanelViewer()` rather than mounting another,
 * and passes `buildingId` so a drawing among them reaches the Drawings viewer.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { track } from "@/lib/posthog/events";

import {
  useDocumentPreview,
  type PreviewableDoc,
} from "@/components/chat/SidebarChat/DocLink/useDocumentPreview";
import { isDwgParentDoc } from "@/domains/documents/hooks/useDrawingViewer";
import { canPreviewFile } from "@/utils/previewUtils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { DocumentViewerDialog } from "../DocumentViewerDialog";
import type { EnclosedDocument } from "../../signing/signatureModel";
import {
  SignaturePanelContext,
  SignaturePanelViewerContext,
  type SignaturePanelActions,
  type SignaturePanelApi,
  type SignaturePanelTarget,
  type SignaturePanelViewerApi,
} from "../../signing/signaturePanelContext";
import { SignaturePanel } from "./SignaturePanel";

export function SignaturePanelProvider({
  children,
  onShare,
  onSign,
  buildingId,
}: {
  children: ReactNode;
  /** Scopes the viewer's Drawings route; only a view that shares the viewer
   *  with rows of its own (and so may open a DWG) needs it. */
  buildingId?: string;
} & SignaturePanelActions) {
  const [target, setTarget] = useState<SignaturePanelTarget | null>(null);
  const api = useMemo<SignaturePanelApi>(
    () => ({
      open: (next) => {
        setTarget(next);
        track("document_signatures_viewed", {});
      },
    }),
    []
  );
  const { t } = useLanguage();
  // The panel always has the document row in hand, so it opens by doc and
  // never takes the hook's filename-lookup path; the building id only scopes
  // the Drawings route.
  const preview = useDocumentPreview(buildingId);
  // Which document the in-flight load is for. Every open goes through
  // `openDocument`, so the last id recorded is always the request the hook is
  // serving (it keeps only the latest); it is recorded only when the hook will
  // actually download — a DWG leaves for the Drawings viewer and an unsupported
  // type is declined, neither touching `isLoading`.
  const [pendingId, setPendingId] = useState<string | null>(null);
  const { openPreviewByDoc: loadPreview, isLoading } = preview;
  const openDocument = useCallback(
    (doc: PreviewableDoc) => {
      if (!isDwgParentDoc(doc) && canPreviewFile(doc.filename, doc.content_type)) {
        setPendingId(String(doc.id));
      }
      return loadPreview(doc);
    },
    [loadPreview]
  );
  const loadingDocumentId = isLoading ? pendingId : null;
  const viewer = useMemo<SignaturePanelViewerApi>(
    () => ({ openPreviewByDoc: openDocument, loadingDocumentId }),
    [openDocument, loadingDocumentId]
  );

  // `openPreviewByDoc` never throws — it parks failures in `preview.error`
  // (missing blob, unconverted DWG child) — so without this the eye/name
  // controls would read as dead. Toast like the panel's download path does,
  // then clear, so the next failed click toasts again.
  const { error: previewError, clearError: clearPreviewError } = preview;
  const openFailed = t.documents.signedContainer.panel.openFailed;
  useEffect(() => {
    if (!previewError) return;
    toast.error(openFailed);
    clearPreviewError();
  }, [previewError, clearPreviewError, openFailed]);

  const openEnclosedDocument = (doc: EnclosedDocument) => {
    // `canOpenEnclosedDocument` gates the control that calls this, so the
    // fields are present — the guard keeps that a type fact rather than a cast.
    if (!doc.filePath || !doc.buildingId || !doc.createdAt) return;
    void openDocument({
      id: doc.id,
      filename: doc.filename,
      file_path: doc.filePath,
      building_id: doc.buildingId,
      content_type: doc.contentType,
      size: doc.size,
      created_at: doc.createdAt,
    });
  };

  return (
    <SignaturePanelContext.Provider value={api}>
      <SignaturePanelViewerContext.Provider value={viewer}>
        {children}
      </SignaturePanelViewerContext.Provider>
      <SignaturePanel
        target={target}
        onClose={() => setTarget(null)}
        onShare={onShare}
        onSign={onSign}
        onOpenDocument={openEnclosedDocument}
      />
      <DocumentViewerDialog
        document={preview.document}
        blobUrl={preview.blobUrl}
        blobData={preview.blobData}
        open={preview.isOpen}
        onOpenChange={(open) => {
          if (!open) preview.closePreview();
        }}
        originalDwgFilePath={preview.originalDwgFilePath ?? undefined}
      />
    </SignaturePanelContext.Provider>
  );
}
